import { Box, Card, CardContent, Chip, Stack, Typography } from '@mui/material';
import { formatMoney } from '../cashbacks/formatMoney';
import { ADJUSTMENT_REASON_LABEL } from './adjustmentsState';
import type { AdjustmentRow as Row } from './adjustmentsModel';

/**
 * One manual balance adjustment in the oversight ledger (D-balance-adjustment): the
 * member, the SIGNED amount (a credit shown positive, a debit negative and in error
 * colour), the reason code, the recorded note, when it happened, and the acting admin
 * joined from the audit trail. Read-only history; the adjustment itself is taken from
 * the user detail.
 */
export const AdjustmentRow = ({ row }: { row: Row }) => {
  const credit = row.amount >= 0;
  const signed = `${credit ? '+' : '-'}${formatMoney(Math.abs(row.amount), row.currency)}`;

  return (
    <Card
      variant="outlined"
      data-testid={`adjustment-row-${row.id}`}
      sx={{ mb: 2 }}
    >
      <CardContent>
        <Stack
          direction="row"
          spacing={2}
          alignItems="center"
          useFlexGap
          flexWrap="wrap"
        >
          <Box sx={{ minWidth: 200 }}>
            <Typography variant="body1" sx={{ fontWeight: 600 }}>
              {row.memberName}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {row.market.toUpperCase()} |{' '}
              {new Date(row.createdAt).toISOString().slice(0, 10)}
            </Typography>
          </Box>
          <Box
            data-testid={`adjustment-amount-${row.id}`}
            sx={{
              fontWeight: 700,
              color: credit ? 'success.main' : 'error.main',
            }}
          >
            {signed}
          </Box>
          <Chip
            size="small"
            variant="outlined"
            label={ADJUSTMENT_REASON_LABEL[row.reasonCode]}
          />
          <Typography variant="caption" color="text.secondary">
            by {row.actor ?? 'not recorded'}
          </Typography>
        </Stack>

        {row.reason && (
          <Typography
            variant="caption"
            color="text.secondary"
            component="div"
            sx={{ mt: 0.5 }}
          >
            Note: {row.reason}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
};
